import React, { ForwardedRef, forwardRef, useState } from "react"
import {
  ChartGroupLayout,
  DashboardChartBase,
  DashboardChartTypeDto,
} from "@features/results/Result/Dashboard/dashboardModel"
import { Chip, Grid, IconButton, SxProps, Tooltip, Typography } from "@mui/material"
import DownloadChartImageDialog from "@features/results/DownloadChartImageDialog"
import ShowChartAsTableButton from "@features/results/Result/ShowChartAsTableButton"
import { DashboardChartDescriptionButton } from "@features/results/Result/Dashboard/DashboardChartDescriptionButton"
import logger from "@utils/logger"
import FullscreenIcon from "@mui/icons-material/Fullscreen"
import RestartAltIcon from "@mui/icons-material/RestartAlt"
import { Visibility, VisibilityOff } from "@mui/icons-material"
import { useDashboardConfig } from "@features/results/Result/Dashboard/dashboardConfig"

const getContainerSx = (chart: DashboardChartBase, layout?: ChartGroupLayout): SxProps => {
  const sx = {
    position: "relative",
    "&:hover": {
      backgroundColor: "#fbf7f7",
      "& .download-chart-button-container": {
        display: "block",
      },
    },
  }

  const height = chart.Type === DashboardChartTypeDto.Threshold ? 730 : 450

  if (!layout) return { maxWidth: 750, height, ...sx }

  // "FillColumns" and "FillRows" are required if there is layout set
  if (!chart.FillColumns || !chart.FillRows) {
    logger.error(`Missing "FillColumns" or "FillRows" for chart with title "${chart.Title}"`)

    return { maxWidth: 750, height, ...sx }
  }

  const [fromColumn = NaN, toColumn = NaN] = chart.FillColumns.split("-").map(Number)
  const [fromRow = NaN, toRow = NaN] = chart.FillRows.split("-").map(Number)

  return {
    gridColumn: `${fromColumn + 1} / ${toColumn + 1}`,
    gridRow: `${fromRow + 1} / ${toRow + 1}`,
    ...sx,
  }
}

interface DashboardChartContainerProps<TChart extends DashboardChartBase> {
  chart: TChart
  layout?: ChartGroupLayout
  children: React.ReactNode
  onOptionChange?: (option: NonNullable<TChart["NodeSelect"]>[number], index: number) => void
  onFullScreen?: React.MouseEventHandler<Element>

  onShowAsTableToggle?: (newValue: boolean) => void
  showAsTable?: boolean

  onShowHistoricDataToggle?: () => void
  showHistoricData?: boolean

  onShowLegendToggle?: () => void
  showLegend?: boolean

  onResetZoom?: () => void

  runIds: string[]
}

// Inner component is there only because forwardRef doesn't preserve generic type parameters when used directly
const DashboardChartContainerInner = <TChart extends DashboardChartBase>(
  props: DashboardChartContainerProps<TChart>,
  ref: ForwardedRef<HTMLDivElement>,
) => {
  const [selected, setSelected] = useState(props.chart.NodeSelect?.[0]?.Option || "")

  const [cfg] = useDashboardConfig()

  const handleOptionChange = (val: string) => {
    if (!props.chart.NodeSelect) return
    const optionIndex = props.chart.NodeSelect.findIndex((v) => v.Option === val) ?? 0
    const option = props.chart.NodeSelect[optionIndex]
    if (!option) return

    props.onOptionChange?.(option, optionIndex)
    setSelected(val)
  }

  return (
    <Grid
      container
      sx={
        cfg.isFullscreen
          ? { height: "100%", width: "calc(100% - 50px)" }
          : getContainerSx(props.chart, props.layout)
      }
      direction="column"
      wrap="nowrap"
      ref={ref}
    >
      <Grid item container justifyContent="space-between" wrap="nowrap">
        <Grid item>
          <Typography variant="h6" component="h3" sx={{ fontWeight: "bold" }}>
            {props.chart.Title}
          </Typography>
        </Grid>
        <Grid container item wrap="nowrap" gap={1} sx={{ width: "unset", pr: "5px" }}>
          {props.onShowHistoricDataToggle && (
            <Grid item>
              <Chip
                data-testid="dashboard-chart-historic-data-toggle"
                component="button"
                label="Historic data"
                avatar={props.showHistoricData ? <Visibility /> : <VisibilityOff />}
                onClick={props.onShowHistoricDataToggle}
                variant="filled"
                color="primary"
                sx={{ borderRadius: "16px !important" }}
              />
            </Grid>
          )}

          {!props.showAsTable && props.onShowLegendToggle && (
            <Grid item>
              <Chip
                data-testid="dashboard-chart-legend-toggle"
                component="button"
                label="Legend"
                avatar={props.showLegend ? <Visibility /> : <VisibilityOff />}
                onClick={() => props.onShowLegendToggle?.()}
                variant="filled"
                color="primary"
                sx={{ borderRadius: "16px !important" }}
              />
            </Grid>
          )}

          {!props.showAsTable && props.onResetZoom && (
            <Grid item>
              <Chip
                data-testid="dashboard-chart-reset-zoom"
                component="button"
                label="Reset zoom"
                avatar={<RestartAltIcon />}
                onClick={props.onResetZoom}
                variant="filled"
                color="primary"
                sx={{ borderRadius: "16px !important" }}
              />
            </Grid>
          )}

          {props.chart.Description && (
            <Grid item>
              <DashboardChartDescriptionButton description={props.chart.Description} />
            </Grid>
          )}

          {!cfg.isFullscreen && (
            <Grid item>
              <Tooltip title="Fullscreen mode">
                <IconButton data-testid="dashboard-chart-fullscreen" onClick={props.onFullScreen}>
                  <FullscreenIcon color="primary" />
                </IconButton>
              </Tooltip>
            </Grid>
          )}
          {props.onShowAsTableToggle && (
            <Grid item>
              <ShowChartAsTableButton
                showAsTable={props.showAsTable ?? false}
                onClick={() => props.onShowAsTableToggle?.(!props.showAsTable)}
                testId="dashboard-chart-show-as-table-toggle"
              />
            </Grid>
          )}
        </Grid>
      </Grid>

      {props.chart.NodeSelect && (
        <Grid item>
          <select
            value={selected}
            onChange={(e) => handleOptionChange(e.target.value)}
            style={{ padding: 5 }}
          >
            {props.chart.NodeSelect.map((s, i) => (
              <option key={`opt-${i}`}>{s.Option}</option>
            ))}
          </select>
        </Grid>
      )}

      <Grid item xs sx={{ overflowY: "auto", overflowX: "hidden" }}>
        {props.children}
      </Grid>

      {!props.showAsTable && (
        <DownloadChartImageDialog
          testId="dashboard-chart-download-image"
          buttonContainerProps={{
            className: "download-chart-button-container",
            sx: cfg.isFullscreen
              ? { position: "absolute", top: 10, right: 35, display: "block" }
              : { position: "absolute", top: 35, right: 5, display: "none" },
          }}
          chartRef={ref}
          defaultFileName={props.chart.Title}
          runReference={props.runIds.join(" vs. ")}
        />
      )}
    </Grid>
  )
}

// Keep correct types when using forwardRef with generic component (can be deleted after update to React 19 after deleting forwardRef)
export const DashboardChartContainer = forwardRef(DashboardChartContainerInner) as <
  TChart extends DashboardChartBase,
>(
  props: DashboardChartContainerProps<TChart> & { ref?: ForwardedRef<HTMLDivElement> },
) => ReturnType<typeof DashboardChartContainerInner>
